import { QuestionBox } from "@ui/organisms/QuestionBox/QuestionBox";
import { Pill } from "@ui/Pill";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { pluralise } from "@utils/text";
import { createEffect, createSignal, Show, untrack } from "solid-js";
import {
	type UseAskPageFormOptions,
	useAskPageForm,
} from "~/modules/askgvt/public-profile/components/useAskPageForm";
import { AnswerCard } from "./AnswerCard";
import {
	askPageMode,
	myAskPageUrl,
	viewerCanAsk,
	viewerIsOwner,
} from "./askPageAccess";
import { AskPageEmptyState } from "./components/AskPageEmptyState";
import { SignupGate } from "./components/SignupGate";
import { SuggestedQuestions } from "./components/SuggestedQuestions";
import { askPageVars } from "./pageVars";

type Props = {
	onJoin?: () => void;
} & UseAskPageFormOptions;

export const AskComposer = (props: Props) => {
	const vars = askPageVars();
	const mode = askPageMode(vars);

	const {
		prompt,
		setPrompt,
		effort,
		setEffort,
		status,
		error,
		disabledReason,
		submit,
	} = useAskPageForm(props);

	const [signupDismissed, setSignupDismissed] = createSignal(false);
	let autoSubmitted = false;

	const noContent = !vars.has_enabled_source;
	const isOwner = viewerIsOwner(vars.status);
	const locked = () =>
		!noContent && !viewerCanAsk(vars.is_paid_ask_page, vars.status);
	const memberName =
		vars.prices.find((price) => price.type === "subscription")
			?.subscription_name ?? "Member";

	createEffect(() => {
		if (props.prefill !== undefined) {
			setPrompt(props.prefill);
		}
		if (props.autoSubmit && props.prefill && !autoSubmitted) {
			autoSubmitted = true;
			untrack(() => void submit());
		}
	});

	return (
		<section class={`askgvt1-ask${locked() ? " askgvt1-ask--paid" : ""}`}>
			<VStack spacing="large">
				<div>
					<HStack align="center" spacing="small">
						<Text
							as="h2"
							family="display"
							size="xlarge"
							weight="bold"
							color="ink"
							style={{ "letter-spacing": "-0.01em", flex: 1 }}
						>
							Ask {vars.influencer_display_name} anything
						</Text>
						<Show when={locked()}>
							<Pill theme="purple" variant="outline" size="xsmall">
								Members only
							</Pill>
						</Show>
						<Show when={mode === "member"}>
							<Pill theme="purple" size="xsmall">
								{memberName} member
							</Pill>
						</Show>
					</HStack>
					<Text size="intermediate" color="fg3">
						{noContent
							? `${vars.influencer_display_name} hasn't added anything to ask about yet. Check back soon.`
							: locked()
								? "Join to ask from this creator's clips."
								: `Answers come from ${vars.influencer_display_name}'s own videos, with the clips to back them up.`}
					</Text>
				</div>
				<Show when={noContent && isOwner}>
					<AskPageEmptyState
						symbol="video_library"
						title="Your page has no sources switched on"
						body="It's still public, but nobody can ask or buy access until a source is on."
						action={{ label: "Manage sources", href: myAskPageUrl("access") }}
					/>
				</Show>
				<QuestionBox
					scope="influencer_pool"
					contextTabs={["videos"]}
					contextSource="pool"
					contextCreators={{ influencerId: vars.influencer_id }}
					askPageHandle={vars.influencer_handle}
					askPageVideoSearch={vars.isSignedIn ? "authenticated" : "public"}
					mentions={!noContent}
					inputLocked={locked()}
					// Locked, the button joins rather than asks, so credits don't
					// come into it until the visitor has joined.
					chargesViewerCredits={
						vars.paysForOwnAskPageQuestions === true && !locked()
					}
					redirectURL=""
					question={prompt()}
					onQuestionChange={setPrompt}
					placeholder={`Ask ${vars.influencer_display_name}…`}
					buttonLabel={locked() ? "Join to ask" : "Ask"}
					buttonTheme={locked() ? "purple" : "mint-grad"}
					buttonSymbol={locked() ? "lock" : "arrow_forward"}
					onEmptySubmit={locked() ? props.onJoin : undefined}
					disabledReason={
						noContent ? "No content to ask about yet." : disabledReason()
					}
					isLoading={status() === "submitting"}
					onAsk={(boxPrompt) => void submit(boxPrompt)}
					creators={[
						{
							handle: vars.influencer_handle,
							displayName: vars.influencer_display_name,
						},
					]}
					contextDetail={`${vars.videoCount} ${pluralise(vars.videoCount, "video")}`}
					effort={effort()}
					onEffortChange={setEffort}
				/>

				<SuggestedQuestions onSelect={setPrompt} />
				<Show when={error()}>
					<AnswerCard question={prompt()} status={"error"} error={error()} />
				</Show>
			</VStack>
			<Show when={!vars.isSignedIn && error() && !signupDismissed()}>
				<SignupGate onClose={() => setSignupDismissed(true)} />
			</Show>
		</section>
	);
};
